/**
 * fh/LanguageSwitcher.tsx
 * Dropdown that lets the user switch between AR / DE / EN / KU.
 * i18n.changeLanguage() triggers the useEffect in App.tsx which
 * updates document.documentElement.dir and .lang automatically.
 */

import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown, Check } from 'lucide-react'

const LANGUAGES = [
  { code: 'ku', label: 'کوردی', short: 'KU' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'AR' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
] as const

export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const current = LANGUAGES.find(l => l.code === i18n.language) ?? LANGUAGES[0]

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${current.label}`}
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '6px 12px', borderRadius: '8px', border: 'none',
          background: 'none', color: 'inherit', fontSize: '14px',
          fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s',
          minHeight: '38px',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--lang-hover, var(--brand-surface-soft))'; e.currentTarget.style.color = 'var(--lang-hover-fg, inherit)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'inherit' }}
      >
        <Globe size={16} style={{ color: 'var(--lang-fg, currentColor)' }} />
        <span>{current.short}</span>
        <ChevronDown size={14} style={{ opacity: 0.6 }} />
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setOpen(false)} />
          <div
            role="listbox"
            aria-label="Select language"
            style={{
              position: 'absolute', top: '100%', insetInlineEnd: 0, marginTop: '4px',
              zIndex: 50, background: '#fff', borderRadius: '12px',
              border: '1px solid #e4e4e7', boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
              minWidth: '160px', padding: '4px', overflow: 'hidden',
            }}
          >
            {LANGUAGES.map(lang => {
              const isActive = lang.code === i18n.language
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    i18n.changeLanguage(lang.code)
                    localStorage.setItem('praxi-lang', lang.code)
                    setOpen(false)
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    padding: '10px 12px', borderRadius: '8px', border: 'none',
                    textAlign: 'start', cursor: 'pointer', fontSize: '14px',
                    transition: 'background 0.1s',
                    background: isActive ? 'var(--brand-surface-soft)' : 'transparent',
                    color: isActive ? 'var(--brand-primary-2)' : '#3f3f46',
                    fontWeight: isActive ? 500 : 400,
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#fafafa' }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                >
                  <span style={{ fontWeight: 600, width: '24px' }}>{lang.short}</span>
                  <span style={{ flex: 1 }}>{lang.label}</span>
                  {isActive && <Check size={14} style={{ color: 'var(--brand-primary-2)' }} />}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
